import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Check, EyeOff, Trash2, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function Stars({ value }) {
  return <span className="inline-flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= value ? "fill-warn text-warn" : "text-line"} />)}</span>;
}

export default function ReviewsAdmin() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const { data: reviews = [] } = useQuery({ queryKey: ["admin-reviews", status], queryFn: () => api.adminListReviews(status) });

  const moderate = async (id, approved) => {
    try { await api.moderateReview(id, approved); toast.success(approved ? "Approved" : "Hidden"); qc.invalidateQueries({ queryKey: ["admin-reviews"] }); }
    catch (err) { toast.error(err.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.deleteReview(id); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-reviews"] }); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <h2 className="font-display text-2xl text-ink mb-4">Reviews Moderation</h2>
      <div className="flex gap-2 mb-5">
        {[["pending", "Pending"], ["approved", "Approved"], ["all", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={`h-9 px-4 rounded-full text-sm font-semibold border ${status === v ? "bg-brand text-white border-brand" : "border-line text-muted2"}`} data-testid={`reviews-filter-${v}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {reviews.length === 0 && <p className="text-muted2 text-sm">No {status === "all" ? "" : status} reviews.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="rounded-xl border border-line p-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-ink text-sm">{r.name}</span>
                  <Stars value={r.rating} />
                  {r.altos_holder && <span className="text-[10px] font-semibold text-ok bg-brandter rounded-full px-2 py-0.5">Altos</span>}
                  {r.approved ? <span className="text-[10px] font-semibold text-ok">● Approved</span> : <span className="text-[10px] font-semibold text-warn">● Pending</span>}
                </div>
                <p className="text-xs text-muted2 mt-0.5">on {r.product_name}</p>
                {r.comment && <p className="text-sm text-ink/80 mt-1">{r.comment}</p>}
              </div>
            </div>
            <div className="flex gap-3 mt-3">
              {!r.approved ? (
                <button onClick={() => moderate(r.id, true)} data-testid={`approve-review-${r.id}`} className="text-sm font-semibold text-ok flex items-center gap-1"><Check size={15} /> Approve</button>
              ) : (
                <button onClick={() => moderate(r.id, false)} data-testid={`hide-review-${r.id}`} className="text-sm font-semibold text-warn flex items-center gap-1"><EyeOff size={15} /> Hide</button>
              )}
              <button onClick={() => remove(r.id)} data-testid={`delete-review-${r.id}`} className="text-sm font-semibold text-err flex items-center gap-1"><Trash2 size={15} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
